import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { SG_GET_EVENTS } from 'constants/actions';
import PageIntro from '../components/PageIntro';

export default function EventsIndex() {
  const dispatch = useDispatch();
  const eventsState = useSelector((s) => s.action_events || {});
  const events = useMemo(() => eventsState?.events || [], [eventsState?.events]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const q = `page=${page}`;
    setLoading(true);
    dispatch({ type: SG_GET_EVENTS, payload: q });
  }, [dispatch, page]);

  useEffect(() => {
    if (events && (events.results || events.length >= 0 || events.count)) setLoading(false);
  }, [events]);

  const list = useMemo(() => events?.results || (Array.isArray(events) ? events : []), [events]);
  const count = useMemo(() => (events?.count ?? (Array.isArray(events) ? events.length : undefined)), [events]);
  const pageSize = useMemo(() => events?.page_size ?? '-', [events]);

  return (
    <div>
      <PageIntro
        title="Events"
        description="Planning workshop events are scheduled and launched for managers to create actions and participate in planning."
        bullets={[
          'Where data comes from: events belong to campaigns and are created by admins or scheduled automatically.',
          'What to expect: scheduled_date controls when invites are sent; launch moves events to active.',
          'Next steps: review scheduled events, launch or cancel as needed, and monitor responses.'
        ]}
      />
      {loading ? <div style={{ color: '#666' }}>Loading events...</div> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
        {list.length ? (
          list.map((e) => {
            const statusColor = e.status === 'active' ? '#16a34a' : (e.status === 'scheduled' ? '#0b5fff' : '#a1a1aa');
            return (
              <div key={e.id} style={{ padding: 12, border: '1px solid #eee', borderRadius: 8, background: '#fff', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name || e.title || '-'}</div>
                      <div style={{ color: '#9ca3af', fontSize: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div title={e.id || ''}>#{e.id?.slice(0, 8) || '-'}</div>
                        <div style={{ fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '4px 8px', borderRadius: 6, fontFamily: 'monospace' }}>{e.id || '-'}</div>
                        <button
                          onClick={() => {
                            const fullId = e.id || '';
                            if (!fullId) return;
                            if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                              navigator.clipboard.writeText(fullId).then(() => setCopiedId(fullId));
                            } else {
                              // fallback
                              const ta = document.createElement('textarea');
                              ta.value = fullId;
                              document.body.appendChild(ta);
                              ta.select();
                              document.execCommand('copy');
                              document.body.removeChild(ta);
                              setCopiedId(fullId);
                            }
                            setTimeout(() => setCopiedId(null), 1500);
                          }}
                          style={{ cursor: 'pointer', padding: '4px 6px', borderRadius: 6, border: '1px solid #e6edf3', background: '#fff' }}
                        >
                          {copiedId === e.id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 12, marginLeft: 6 }}>{e.event_type || e.type || '-'}</div>
                    </div>
                    <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>
                      <span>{e.campaign_name || e.campaign?.name || '-'}</span>
                      <span style={{ marginLeft: 8, color: '#9ca3af' }}>• org: {e.organization?.id ?? '-'}</span>
                      <span style={{ marginLeft: 8, color: '#9ca3af' }}>• seq: {e.sequence_number ?? '-'}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div style={{ padding: '6px 10px', borderRadius: 999, background: '#fff', border: `1px solid ${statusColor}`, color: statusColor, fontWeight: 700 }}>{e.status}</div>
                    {e.id && e.id !== 'undefined' ? (
                      <Link to={`${e.id}`} style={{ color: '#0b5fff', fontWeight: 700 }}>View</Link>
                    ) : (
                      <div style={{ color: '#9ca3af' }}>No id</div>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>Scheduled: {e.scheduled_date ? new Date(e.scheduled_date).toLocaleString() : '-'}</div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>Launch: {e.launch_date ? new Date(e.launch_date).toLocaleString() : '-'}</div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>Close: {e.close_date ? new Date(e.close_date).toLocaleString() : '-'}</div>

                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: 12 }}>Invited</div>
                      <div style={{ fontWeight: 700 }}>{e.total_invited ?? 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: 12 }}>Responded</div>
                      <div style={{ fontWeight: 700 }}>{e.total_responded ?? 0}</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ color: '#666', fontSize: 12 }}>Rate</div>
                      <div style={{ fontWeight: 700 }}>{e.response_rate_percentage ?? (e.response_rate != null ? `${e.response_rate}%` : '0%')}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ padding: 18, border: '1px dashed #eee', borderRadius: 8, color: '#666' }}>No events</div>
        )}
      </div>

      <div style={{ marginTop: 10 }}>
        <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>Previous</button>
        <span style={{ margin: '0 12px' }}>Page {page} — {count ?? (list.length || 0)} items</span>
        <button onClick={() => setPage(page + 1)} disabled={!events?.next}>Next</button>

        <span style={{ marginLeft: 12 }}>Page size: <strong>{pageSize}</strong></span>
      </div>
    </div>
  );
}
